import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap } from 'rxjs';
import { environment } from '@env';
import { SesionServicio } from '@nucleo/sesion/sesion-servicio';
import { SesionApiServicio } from '@nucleo/sesion/sesion-api-servicio';
import { ConfiguracionApiIndividual } from '@nucleo/modelos/configuracion-api-dto';
import { OMITIR_AUTORIZACION } from '@nucleo/http/contexto-peticion-util';

const PREFIJO_AUTORIZACION = 'Bearer';
const CABECERA_AUTORIZACION = 'Authorization';

/**
 * Agrega a las peticiones dirigidas a las APIs del ambiente sus cabeceras declaradas y el token `Bearer`.
 * Si el access token está por vencer, primero lo renueva (`/auth/refresh`) y luego envía la petición.
 * Las peticiones marcadas con `OMITIR_AUTORIZACION` (`/auth/*`) van sin token. Otros dominios no se tocan.
 */
export const cabecerasApiInterceptor: HttpInterceptorFn = (peticion, siguiente) => {
  const apisConfiguradas: ConfiguracionApiIndividual[] = Object.values(environment.apisBase);
  const configuracionApi = apisConfiguradas.find((api) => peticion.url.startsWith(api.url));
  if (!configuracionApi) {
    return siguiente(peticion);
  }

  const cabecerasBase: Record<string, string> = Object.fromEntries(
    configuracionApi.cabeceras
      // Con FormData (subida de archivos) el navegador fija `Content-Type` con su frontera: no se debe forzar JSON.
      .filter((cabecera) => !(peticion.body instanceof FormData && cabecera.clave.toLowerCase() === 'content-type'))
      .map((cabecera) => [cabecera.clave, cabecera.valor]),
  );
  if (peticion.context.get(OMITIR_AUTORIZACION)) {
    return siguiente(peticion.clone({ setHeaders: cabecerasBase }));
  }

  const servicioSesion = inject(SesionServicio);
  const servicioSesionApi = inject(SesionApiServicio);
  const enviarConToken = () => {
    const token = servicioSesion.tokenAcceso();
    const cabeceras = token
      ? { ...cabecerasBase, [CABECERA_AUTORIZACION]: `${PREFIJO_AUTORIZACION} ${token}` }
      : cabecerasBase;
    return siguiente(peticion.clone({ setHeaders: cabeceras }));
  };

  if (servicioSesion.estaAutenticado() && servicioSesion.accesoPorVencer()) {
    return servicioSesionApi.renovar().pipe(switchMap(() => enviarConToken()));
  }
  return enviarConToken();
};
